import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import BgOrnaments from "@/components/vu/BgOrnaments";

const IMG_MAIN = "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const IMG_ACCENT = "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=700";

export default function About() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y1 = useTransform(scrollYProgress, [0, 1], ["-3%", "5%"]);

    return (
        <section ref={ref} data-testid="about-section" className="relative py-[110px] md:py-[140px] border-b border-[#1F3A2B] overflow-hidden">
            <BgOrnaments variant="dots" />
            <div className="max-w-[1240px] mx-auto px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                {/* Left: image pair */}
                <div className="lg:col-span-5 relative">
                    <motion.div style={{ y: y1 }} className="relative aspect-[4/5] rounded-[28px] overflow-hidden border border-[#1F3A2B] warm-glow">
                        <img src={IMG_MAIN} alt="Team collaborating" className="w-full h-full object-cover" />
                    </motion.div>
                    <div className="absolute -bottom-8 -right-4 md:-right-8 w-[52%] aspect-square rounded-2xl overflow-hidden border-4 border-white soft-shadow hidden md:block">
                        <img src={IMG_ACCENT} alt="Local business" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -top-3 -left-3 bg-[#152A20] border border-[#1F3A2B] px-3 py-1.5 rounded-full text-[11px] text-[#F5F0E8] flex items-center gap-2 soft-shadow">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#DFFF3D] vu-live-dot" /> A senior team, not juniors
                    </div>
                </div>

                {/* Right */}
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-10%" }} transition={{ duration: 0.9 }} className="lg:col-span-7">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-[#2A4A38] bg-[#152A20]/70 backdrop-blur rounded-full text-[11.5px] text-[#A6BDB0] mb-6">
                        <span className="w-[7px] h-[7px] rounded-full bg-[#DFFF3D]" /> About V&amp;U
                    </div>
                    <h2 className="font-display text-[#F5F0E8] tracking-tight leading-[0.98]" style={{ fontSize: "clamp(2.4rem, 4.6vw, 3.8rem)" }}>
                        One team. One retainer.<br />
                        <span className="italic-serif text-[#DFFF3D]">Every channel covered.</span>
                    </h2>
                    <p className="mt-8 text-[17px] text-[#A6BDB0] leading-[1.7] max-w-[560px]">
                        V&amp;U isn't a software shop or a big-agency assembly line. We're a small senior team that builds your website, manages your Google presence, runs your ads, writes your email — and now answers every call with a 24/7 AI voice agent that qualifies leads and books appointments while you sleep. One team. No cracks between vendors.
                    </p>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 border-t border-[#1F3A2B] pt-10">
                        {[["09+", "Years shipping"], ["212+", "Retainer clients"], ["$2.4M+", "Ad spend managed"], ["4.1×", "Average ROAS"]].map(([k, v]) => (
                            <div key={v}>
                                <div className="font-display text-[#F5F0E8] text-[2rem] md:text-[2.2rem] leading-none">{k}</div>
                                <div className="text-[12px] text-[#7A9088] mt-2.5 leading-tight">{v}</div>
                            </div>
                        ))}
                    </div>
                    <a href="#services" className="vu-btn mt-10 inline-flex items-center gap-2 border border-[#F5F0E8]/15 bg-[#152A20] text-[#F5F0E8] px-6 py-3 rounded-full font-semibold text-[14px] hover:bg-[#152A20]">
                        See how we work <i className="fa-solid fa-arrow-right text-xs" />
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
